import {useCallback, useEffect, useRef} from 'react';

import {
  type AvailableFilesQueryQuery,
  type AvailableFilesQueryQueryVariables,
  useAvailableFilesQueryQuery,
} from '../../../generated/graphql';
import {NetworkStatus} from '../../../state/graphql/apolloWithAbort';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {propagateErrorsContext} from '../../../util/errors';
import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../../MediaCard';
import {getObjectURLForBlob, recordBackgroundBlobFetchFailure} from './blob';
import {useFileDataTracking} from './fileDataTracking';
import {type TileMediaErrorKind, type TileMediaStatus} from './inMemoryCache';
import {getPanelFilePathKey, useWorkspaceBlobStore} from './panelBlobStore';
import {getRequestedPathSpec} from './paths';
import {type FilePathSpec} from './types';
import {shouldNotLogBlobFetchSuccess, shouldSkipBlobFetch} from './update';
import {useBlob} from './useBlob';

/**
 * Idle status matching `useTileMedia` when a spec has never been requested.
 */
export const IDLE_TILE_MEDIA_STATUS: TileMediaStatus = {
  isLoading: false,
  isRequested: false,
  isLoaded: false,
  hasError: false,
  lastRequestedAt: 0,
  lastUpdatedAt: 0,
};

/**
 * GraphQL requires `filenames: [String!]!`. Used when the spec has no path
 * at `mediaStep` (missing history row or empty path).
 */
const EMPTY_FILENAMES: AvailableFilesQueryQueryVariables['filenames'] = [];

/** Matches `useTileMedia`: cap load-error refetches so a dead URL cannot loop. */
const MAX_LOAD_ERROR_RETRIES = 2;

/** RUM action when panel store metadata TTL elapsed and refresh starts. */
const METADATA_EXPIRED_RUM_ACTION = 'mediaBrowserPanelTile.metadataExpired';

type AvailableFileNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<AvailableFilesQueryQuery['project']>['run']
    >['files']
  >['edges'][number]['node']
>;

export type UseFileDataOptions = {
  skip?: boolean;
  disableVideoStream?: boolean;
};

export type UseFileDataResult = {
  status: TileMediaStatus;
  tileMedia: TileMedia | null;
  tracking: TileMediaTracking | null;
  invalidateAndRefetch: () => void;
};

/**
 * Per-tile file-data hook.
 *
 * Resolves `filenames` from the history row at `tileMediaSpec.mediaStep`.
 * Issues one generated `useAvailableFilesQueryQuery` with
 * `cache-first`. Skipped when the requested step has no path
 * or when `options.skip` is set. Wired into `useTileMedia` behind
 * `media_panel_react_fetch`.
 *
 * `cache-first` strategy only issues a request if the data is not in the cache.
 * Using `cache-and-network` returns the cached entry, and updates the cache in the background.
 * Each request generates a new directURL, so `cache-first` avoids duplicate requests.
 */
export function useFileData(
  tileMediaSpec: TileMediaSpec,
  options?: UseFileDataOptions
): UseFileDataResult {
  const disableVideoStream = options?.disableVideoStream ?? false;
  const {entityName, projectName, runName} = tileMediaSpec.runSignature;
  const pathSpec = getRequestedPathSpec(tileMediaSpec);
  const filenames = pathSpec != null ? [pathSpec.filePath] : EMPTY_FILENAMES;
  const skip = Boolean(options?.skip) || filenames.length === 0;
  const loadErrorRetryCountRef = useRef(0);
  const observedInitialNetworkLoadRef = useRef(false);
  const queryKeyRef = useRef('');
  const filePathKey =
    pathSpec != null
      ? getPanelFilePathKey(tileMediaSpec.runSignature, pathSpec.filePath)
      : null;

  const queryKey = `${entityName}\0${projectName}\0${runName}\0${filenames.join('\0')}`;
  // oxlint-disable-next-line react/refs
  if (queryKeyRef.current !== queryKey) {
    // oxlint-disable-next-line react/refs
    queryKeyRef.current = queryKey;
    // oxlint-disable-next-line react/refs
    observedInitialNetworkLoadRef.current = false;
  }

  const hasMetadata = useWorkspaceBlobStore(state => state.hasMetadata);
  const isMetadataExpired = useWorkspaceBlobStore(
    state => state.isMetadataExpired
  );
  const takeMetadataExpiryEvent = useWorkspaceBlobStore(
    state => state.takeMetadataExpiryEvent
  );
  const getMetadataDirectURL = useWorkspaceBlobStore(
    state => state.getMetadataDirectURL
  );
  const syncMetadata = useWorkspaceBlobStore(state => state.syncMetadata);

  const needsMetadataRefresh =
    !skip &&
    filePathKey != null &&
    hasMetadata(filePathKey) &&
    isMetadataExpired(filePathKey);

  if (needsMetadataRefresh && takeMetadataExpiryEvent(filePathKey)) {
    recordRumAction(METADATA_EXPIRED_RUM_ACTION, {
      runName: tileMediaSpec.runSignature.runName,
      mediaKey: tileMediaSpec.mediaKey,
      mediaStep: tileMediaSpec.mediaStep,
      mediaType: tileMediaSpec.mediaType,
    });
  }

  const {data, error, loading, networkStatus, refetch} =
    useAvailableFilesQueryQuery({
      fetchPolicy: needsMetadataRefresh ? 'cache-and-network' : 'cache-first',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip,
      context: propagateErrorsContext(),
      variables: {
        entityName,
        projectName,
        runName,
        filenames,
        includeDirectUrl: true,
        includeThumbnails: true,
        includeHlsSrc: true,
      },
    });

  if (!skip && networkStatus === NetworkStatus.loading && data == null) {
    // oxlint-disable-next-line react/refs
    observedInitialNetworkLoadRef.current = true;
  }

  const fileNode =
    pathSpec != null ? getExistingFileNode(data, pathSpec.filePath) : null;

  if (!skip && filePathKey != null && fileNode?.directUrl != null) {
    const priorDirectURL = getMetadataDirectURL(filePathKey);
    const isUrlRotation =
      priorDirectURL != null && priorDirectURL !== fileNode.directUrl;

    if (!hasMetadata(filePathKey) || isUrlRotation) {
      syncMetadata(filePathKey, {
        directURL: fileNode.directUrl,
      });
    }
  }

  const urlTileMedia =
    pathSpec != null && fileNode != null
      ? tileMediaFromUnifiedFile(tileMediaSpec, pathSpec, fileNode)
      : null;

  const filePath = pathSpec?.filePath;
  const refreshDirectURL = useCallback(async () => {
    const {data: fresh} = await refetch();
    return filePath != null
      ? getExistingFileNode(fresh, filePath)?.directUrl
      : null;
  }, [filePath, refetch]);

  const {tileMedia, blobPending, blobError, blobErrorKind, resetFetchedBlob} =
    useStoredFileBlob(
      urlTileMedia,
      tileMediaSpec,
      pathSpec,
      disableVideoStream,
      refreshDirectURL
    );

  const uploadPending =
    pathSpec != null &&
    data != null &&
    urlTileMedia == null &&
    isUploadPendingFile(data, pathSpec.filePath);

  const {tracking, resetTrackingTimestamps} = useFileDataTracking({
    skip,
    availableFilesData: data,
    tileMedia,
    tileMediaSpec,
    disableVideoStream,
    // oxlint-disable-next-line react/refs
    observedInitialNetworkLoad: observedInitialNetworkLoadRef.current,
  });
  const status = statusFromQuery({
    explicitSkip: Boolean(options?.skip),
    missingRequestedPath: pathSpec == null && !options?.skip,
    errorMessage: error?.message,
    loading,
    hasData: data != null,
    tileMedia,
    blobPending,
    blobError,
    blobErrorKind,
    uploadPending,
  });

  const invalidateAndRefetch = useCallback(() => {
    if (loadErrorRetryCountRef.current >= MAX_LOAD_ERROR_RETRIES) {
      return;
    }
    loadErrorRetryCountRef.current += 1;
    resetTrackingTimestamps();
    resetFetchedBlob();
    void refetch();
  }, [refetch, resetFetchedBlob, resetTrackingTimestamps]);

  return {
    status,
    tileMedia,
    tracking,
    invalidateAndRefetch,
  };
}

/**
 * Workspace-scoped blob bytes via `useBlob`. Same rules as `update.ts`: skip
 * images with an internal URL; HLS still downloads in the background but does
 * not block `isLoaded`.
 */
function useStoredFileBlob(
  urlTileMedia: TileMedia | null,
  tileMediaSpec: TileMediaSpec,
  pathSpec: FilePathSpec | null,
  disableVideoStream: boolean,
  onUrlExpired: () => Promise<string | null | undefined>
): {
  tileMedia: TileMedia | null;
  blobPending: boolean;
  blobError: string | undefined;
  blobErrorKind: TileMediaErrorKind | undefined;
  resetFetchedBlob: () => void;
} {
  const skipBlob =
    urlTileMedia != null && shouldSkipBlobFetch(urlTileMedia, tileMediaSpec);
  const waitForBlob =
    urlTileMedia != null &&
    !shouldNotLogBlobFetchSuccess(
      urlTileMedia,
      tileMediaSpec,
      disableVideoStream
    );

  const blobSnapshot = useBlob({
    runSignature: tileMediaSpec.runSignature,
    filePath: pathSpec?.filePath,
    directURL: skipBlob || urlTileMedia == null ? null : urlTileMedia.directURL,
    logFailure: waitForBlob,
    onUrlExpired,
  });
  const invalidate = useWorkspaceBlobStore(state => state.invalidate);

  const backgroundBlobError =
    !waitForBlob && blobSnapshot.status === 'error'
      ? blobSnapshot.error
      : undefined;
  useEffect(() => {
    if (backgroundBlobError != null) {
      recordBackgroundBlobFetchFailure(
        tileMediaSpec.mediaType,
        backgroundBlobError
      );
    }
  }, [backgroundBlobError, tileMediaSpec.mediaType]);

  const resetFetchedBlob = useCallback(() => {
    if (pathSpec == null) {
      return;
    }
    invalidate(
      getPanelFilePathKey(tileMediaSpec.runSignature, pathSpec.filePath)
    );
  }, [invalidate, pathSpec, tileMediaSpec.runSignature]);

  if (urlTileMedia == null) {
    return {
      tileMedia: null,
      blobPending: false,
      blobError: undefined,
      blobErrorKind: undefined,
      resetFetchedBlob,
    };
  }

  if (
    blobSnapshot.status === 'ready' &&
    blobSnapshot.blob != null &&
    blobSnapshot.objectURL != null
  ) {
    return {
      tileMedia: {
        ...urlTileMedia,
        isNullBlob: false,
        blob: blobSnapshot.blob,
        objectURL: blobSnapshot.objectURL,
      },
      blobPending: false,
      blobError: undefined,
      blobErrorKind: undefined,
      resetFetchedBlob,
    };
  }

  // A background blob (streamed video) only backs the download button, so its
  // failure must not turn an already-playing tile into an error state.
  const blobError =
    waitForBlob && blobSnapshot.status === 'error'
      ? blobSnapshot.error
      : undefined;
  const blobErrorKind =
    waitForBlob && blobSnapshot.status === 'error'
      ? blobSnapshot.errorKind
      : undefined;

  // Include `idle`: fetchIfNeeded only starts in a useEffect, so the first
  // paint after metadata lands still has IDLE_PANEL_FILE_BLOB. Treating only
  // `loading` as pending marked the tile complete with an empty nullBlob and
  // let consumers (e.g. PointCloud) parse "" before the download started.
  return {
    tileMedia: urlTileMedia,
    blobPending:
      waitForBlob && blobError == null && blobSnapshot.status !== 'ready',
    blobError,
    blobErrorKind,
    resetFetchedBlob,
  };
}

function getFileNodeByPath(
  data: AvailableFilesQueryQuery | undefined,
  filePath: string
): AvailableFileNode | null {
  const nodes =
    data?.project?.run?.files?.edges
      ?.map(edge => edge.node)
      .filter((node): node is AvailableFileNode => node != null) ?? [];
  return nodes.find(node => node.name === filePath) ?? null;
}

function isUploadPendingFile(
  data: AvailableFilesQueryQuery,
  filePath: string
): boolean {
  const node = getFileNodeByPath(data, filePath);
  return node != null && Number(node.sizeBytes) === 0;
}

function getExistingFileNode(
  data: AvailableFilesQueryQuery | undefined,
  filePath: string
): AvailableFileNode | null {
  const node = getFileNodeByPath(data, filePath);
  if (node == null || Number(node.sizeBytes) <= 0) {
    return null;
  }
  return node;
}

function tileMediaFromUnifiedFile(
  tileMediaSpec: TileMediaSpec,
  pathSpec: FilePathSpec,
  node: AvailableFileNode
): TileMedia | null {
  if (node.directUrl == null) {
    return null;
  }
  const nullBlob = new Blob();
  return {
    mediaKey: tileMediaSpec.mediaKey,
    step: tileMediaSpec.mediaStep,
    index: tileMediaSpec.mediaIndex,
    historyRow: pathSpec.historyRow,
    filePath: pathSpec.filePath,
    directURL: node.directUrl,
    internalURL: node.url ?? undefined,
    mediaSliderKey: tileMediaSpec.mediaSliderKey,
    mediaSliderKeyValue: tileMediaSpec.mediaSliderKeyValue,
    thumbnails: node.thumbnails ?? undefined,
    hlsSrc: node.hlsSrc,
    isNullBlob: true,
    blob: nullBlob,
    objectURL: getObjectURLForBlob(nullBlob),
  };
}

function statusFromQuery({
  explicitSkip,
  missingRequestedPath,
  errorMessage,
  loading,
  hasData,
  tileMedia,
  blobPending,
  blobError,
  blobErrorKind,
  uploadPending,
}: {
  explicitSkip: boolean;
  missingRequestedPath: boolean;
  errorMessage: string | undefined;
  loading: boolean;
  hasData: boolean;
  tileMedia: TileMedia | null;
  blobPending: boolean;
  blobError: string | undefined;
  blobErrorKind: TileMediaErrorKind | undefined;
  uploadPending: boolean;
}): TileMediaStatus {
  if (explicitSkip) {
    return IDLE_TILE_MEDIA_STATUS;
  }
  if (missingRequestedPath) {
    return requestedError(
      'No path specification found for media',
      'media_missing_data'
    );
  }
  if (errorMessage != null) {
    return requestedError(errorMessage, 'media_query_failed');
  }
  if (blobError != null) {
    return requestedError(
      blobError,
      blobErrorKind ?? 'media_blob_fetch_failed'
    );
  }
  if (tileMedia != null) {
    return withBlobPending(loadedStatus(), blobPending);
  }
  if (hasData) {
    if (uploadPending) {
      return requestedError(
        'No direct URL found for media',
        'media_upload_pending'
      );
    }
    return requestedError('No direct URL found for media', 'media_missing_url');
  }
  return {
    isLoading: loading,
    isRequested: true,
    isLoaded: false,
    hasError: false,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };
}

function loadedStatus(): TileMediaStatus {
  return {
    isLoading: false,
    isRequested: true,
    isLoaded: true,
    hasError: false,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };
}

function withBlobPending(
  status: TileMediaStatus,
  blobPending: boolean
): TileMediaStatus {
  if (!blobPending || status.hasError || !status.isLoaded) {
    return status;
  }
  return {
    ...status,
    isLoading: true,
    isLoaded: false,
  };
}

function requestedError(
  errorMessage: string,
  errorKind: TileMediaErrorKind
): TileMediaStatus {
  return {
    isLoading: false,
    isRequested: true,
    isLoaded: false,
    hasError: true,
    errorMessage,
    errorKind,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };
}
